import React, { useContext, useEffect } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/modern/components/ui/dialog';
import { Button } from '@/modern/components/ui/button';
import { Label } from '@/modern/components/ui/label';
import { DatePicker } from '@/modern/components/ui/date-picker';
import { Input } from '@/modern/components/ui/input';
import { Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/modern/components/ui/select';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { format, differenceInDays } from 'date-fns';
import BryntumContext from '@/react/context/bryntumContext';
import { DateHelper } from '@bryntum/schedulerpro';
import TaskEditorColorMapping from '@/other/constants/TaskEditorColors';

// ============================================================================
// TYPE DEFINITIONS & SCHEMA
// ============================================================================

const planningSchema = z.object({
  name: z.string().min(1, 'Plan element name is required'),
  startDate: z.date(),
  startTime: z.string().min(1, 'Start time is required'),
  endDate: z.date(),
  endTime: z.string().min(1, 'End time is required'),
  color: z.string().min(1, 'Color is required'),
}).refine((data) => {
  const startDateTime = new Date(data.startDate);
  const [startHours, startMinutes] = data.startTime.split(':');
  startDateTime.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

  const endDateTime = new Date(data.endDate);
  const [endHours, endMinutes] = data.endTime.split(':');
  endDateTime.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);

  return endDateTime > startDateTime;
}, {
  message: 'End date/time must be after start date/time',
  path: ['endDate'],
});

export type PlanningFormData = z.infer<typeof planningSchema>;

interface PlanningEditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  planningId?: string;
  taskId: string;
  defaultFormValues?: Partial<PlanningFormData>;
}

// Preset colors from Task Editor (Material Design palette)
const PRESET_COLORS = TaskEditorColorMapping.map((item) => ({
  name: item.color.toUpperCase(), // Display in uppercase for consistency
  value: item.color.toUpperCase(), // Store in uppercase for consistency
}));

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const PlanningEditModal: React.FC<PlanningEditModalProps> = ({
  open,
  onOpenChange,
  mode,
  planningId,
  taskId,
  defaultFormValues
}) => {
  const { project } = useContext(BryntumContext);
  const [isLoading, setIsLoading] = React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<PlanningFormData>({
    resolver: zodResolver(planningSchema),
    defaultValues: {
      name: '',
      startDate: new Date(),
      startTime: '08:00',
      endDate: DateHelper.add(new Date(), 1, 'day'),
      endTime: '17:00',
      color: '#ef4444',
      ...defaultFormValues
    }
  });

  const watchedStartDate = watch('startDate');
  const watchedEndDate = watch('endDate');

  // Calculate duration
  const calculateDuration = () => {
    if (!watchedStartDate || !watchedEndDate) {
      return 'N/A';
    }

    const days = differenceInDays(watchedEndDate, watchedStartDate) + 1; // +1 to include both start and end days
    return `${days} day${days !== 1 ? 's' : ''}`;
  };

  // Load existing planning data in edit mode
  useEffect(() => {
    if (open && mode === 'edit' && planningId && project) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const planningStore = (project as any).taskPlanningDatesStore;
      const planning = planningStore?.getById(planningId);

      if (planning) {
        const startDate = new Date(planning.startDate);
        const endDate = new Date(planning.endDate);

        reset({
          name: planning.name || '',
          startDate: startDate,
          startTime: format(startDate, 'HH:mm'),
          endDate: endDate,
          endTime: format(endDate, 'HH:mm'),
          color: planning.color?.toUpperCase() || PRESET_COLORS[0].value,
        });
      }
    } else if (open && mode === 'create') {
      // Reset to defaults for create mode
      const now = new Date();
      const tomorrow = DateHelper.add(now, 1, 'day');
      reset({
        name: '',
        startDate: now,
        startTime: '08:00',
        endDate: tomorrow,
        endTime: '17:00',
        color: PRESET_COLORS[0].value,
      });
    }
  }, [open, mode, planningId, project, reset]);

  const onSubmit = async (data: PlanningFormData) => {
    if (!project) return;

    try {
      setIsLoading(true);

      // Combine date and time for start and end
      const startDateTime = new Date(data.startDate);
      const [startHours, startMinutes] = data.startTime.split(':');
      startDateTime.setHours(parseInt(startHours), parseInt(startMinutes), 0, 0);

      const endDateTime = new Date(data.endDate);
      const [endHours, endMinutes] = data.endTime.split(':');
      endDateTime.setHours(parseInt(endHours), parseInt(endMinutes), 0, 0);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const planningStore = (project as any).taskPlanningDatesStore;

      if (mode === 'edit' && planningId) {
        // Update existing planning date
        const planning = planningStore.getById(planningId);
        if (planning) {
          planning.set({
            name: data.name,
            startDate: startDateTime.toISOString(),
            endDate: endDateTime.toISOString(),
            color: data.color,
          });
        }
      } else {
        // Create new planning date
        const planningRecord = planningStore.createRecord({
          task: taskId,
          name: data.name,
          startDate: startDateTime.toISOString(),
          endDate: endDateTime.toISOString(),
          color: data.color,
        });

        planningStore.add(planningRecord);
      }

      onOpenChange(false);
    } catch (error) {
      console.error('Error saving planning date:', error);
      alert('Failed to save planning date. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[600px] max-h-[85vh] flex flex-col p-0 shadow-none">
        <DialogHeader className="px-6 py-6 flex-shrink-0 bg-background border-b">
          <DialogTitle className="text-base font-normal">
            {mode === 'create' ? 'Add Planning Date' : 'Edit Planning Date'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto px-6 space-y-6 flex-1" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
          {/* Planning Details Section */}
          <div className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-sm font-medium">Plan Element Name *</Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Input
                  id="name"
                  placeholder="Enter plan element name"
                  autoComplete="off"
                  {...field}
                  className={errors.name ? 'border-destructive' : ''}
                />
              )}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          {/* Start Date & Time - Two columns */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Start Date *</Label>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select start date"
                    error={errors.startDate?.message}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="startTime" className="text-sm font-medium">Start Time *</Label>
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                      id="startTime"
                      type="time"
                      inputSize="sm"
                      autoComplete="off"
                      {...field}
                      className={`pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:cursor-pointer ${errors.startTime ? 'border-destructive' : ''}`}
                    />
                  </div>
                )}
              />
              {errors.startTime && (
                <p className="text-sm text-destructive">{errors.startTime.message}</p>
              )}
            </div>
          </div>

          {/* End Date & Time - Two columns */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">End Date *</Label>
              <Controller
                name="endDate"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select end date"
                    error={errors.endDate?.message}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endTime" className="text-sm font-medium">End Time *</Label>
              <Controller
                name="endTime"
                control={control}
                render={({ field }) => (
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                      id="endTime"
                      type="time"
                      inputSize="sm"
                      autoComplete="off"
                      {...field}
                      className={`pl-10 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:w-5 [&::-webkit-calendar-picker-indicator]:h-5 [&::-webkit-calendar-picker-indicator]:cursor-pointer ${errors.endTime ? 'border-destructive' : ''}`}
                    />
                  </div>
                )}
              />
              {errors.endTime && (
                <p className="text-sm text-destructive">{errors.endTime.message}</p>
              )}
            </div>
          </div>

          {/* Color & Duration - Two columns */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Color *</Label>
              <Controller
                name="color"
                control={control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className={errors.color ? 'border-destructive' : ''}>
                      <SelectValue placeholder="Select color" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRESET_COLORS.map((color) => (
                        <SelectItem key={color.value} value={color.value}>
                          <div className="flex items-center gap-2">
                            <div
                              className="h-4 w-4 rounded border flex-shrink-0"
                              style={{ backgroundColor: color.value }}
                            />
                            <span>{color.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.color && (
                <p className="text-sm text-destructive">{errors.color.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Duration</Label>
              <div className="flex h-10 w-full rounded-md border border-input bg-muted px-3 py-2 text-sm">
                {calculateDuration()}
              </div>
            </div>
          </div>
          </div>
          </div>

          <DialogFooter className="gap-2 px-6 py-4 flex-shrink-0 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PlanningEditModal;
